// Renders the server-parsed RichBlock tree as React elements.
//
// Mirror of packages/widget/src/views/rich-text.ts — same node union, same
// class semantics, so an AI reply looks the same in the agent's transcript as
// it does in the visitor's bubble. Plain elements only: the tree cannot carry
// markup, and hrefs are re-validated on the way out.

import { safeHref } from '@prakash-software/shared';
import type { RichBlock, RichInline } from '@prakash-software/shared';
import styles from './RichText.module.css';

export function RichText({ blocks }: { blocks: RichBlock[] }): React.ReactElement {
  return <>{blocks.map((b, i) => renderBlock(b, i))}</>;
}

function renderBlock(block: RichBlock, key: number): React.ReactNode {
  switch (block.kind) {
    case 'paragraph':
      return (
        <p key={key} className={styles.p}>
          {renderInline(block.children)}
        </p>
      );
    case 'heading': {
      // h1-h3 in the source map to h4-h6 in the DOM — a chat bubble sits deep
      // in the page outline and must not claim top-level headings.
      const Tag = `h${block.level + 3}` as 'h4' | 'h5' | 'h6';
      return (
        <Tag key={key} className={styles.h} data-level={block.level}>
          {renderInline(block.children)}
        </Tag>
      );
    }
    case 'list': {
      const items = block.items.map((item, i) => (
        <li key={i}>{item.map((child, j) => renderBlock(child, j))}</li>
      ));
      return block.ordered ? (
        <ol key={key} className={styles.list} start={block.start ?? 1}>
          {items}
        </ol>
      ) : (
        <ul key={key} className={styles.list}>
          {items}
        </ul>
      );
    }
    case 'codeblock':
      return (
        <pre key={key} className={styles.pre}>
          <code>{block.text}</code>
        </pre>
      );
    case 'quote':
      return (
        <blockquote key={key} className={styles.quote}>
          {block.children.map((child, i) => renderBlock(child, i))}
        </blockquote>
      );
    case 'rule':
      return <hr key={key} className={styles.rule} />;
  }
}

function renderInline(nodes: RichInline[]): React.ReactNode[] {
  return nodes.map((node, i) => {
    switch (node.kind) {
      case 'text':
        return node.text;
      case 'br':
        return <br key={i} />;
      case 'code':
        return (
          <code key={i} className={styles.code}>
            {node.text}
          </code>
        );
      case 'strong':
        return <strong key={i}>{renderInline(node.children)}</strong>;
      case 'em':
        return <em key={i}>{renderInline(node.children)}</em>;
      case 'link': {
        // Re-check the scheme: trees can also come from a cached payload, and
        // an href is the one field here that becomes executable.
        const href = safeHref(node.href);
        if (!href) return <span key={i}>{renderInline(node.children)}</span>;
        return (
          <a key={i} href={href} target="_blank" rel="noopener noreferrer" className={styles.link}>
            {renderInline(node.children)}
          </a>
        );
      }
    }
  });
}
